import React, { useState } from 'react';
import { Link2, Linkedin, Facebook, Mail, Check, Instagram, Share2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const XIcon: React.FC<{ size?: number; className?: string }> = ({ size = 18, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" fill="currentColor"/>
  </svg>
);

interface SocialShareProps {
  url: string;
  title: string;
  excerpt?: string;
}

const SocialShare: React.FC<SocialShareProps> = ({ url, title, excerpt }) => {
  const [copied, setCopied] = useState(false);
  const canShare = typeof navigator !== 'undefined' && navigator.share;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  const handleNativeShare = async () => {
    try {
      await navigator.share({
        title: title,
        text: excerpt || title,
        url: url,
      });
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        console.error('Error sharing:', error);
      }
    }
  };

  const shareLinks = {
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    x: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    instagram: `https://www.instagram.com/`,
    email: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${excerpt || ''}\n\n${url}`)}`
  };

  return (
    <div className="flex items-center space-x-2">
      <span className="text-sm font-medium text-gray-600">Share:</span>

      <button
        onClick={handleCopyLink}
        className="p-2 hover:bg-gray-100 rounded-lg transition-colors relative"
        aria-label="Copy link"
      >
        {copied ? <Check size={18} className="text-green-600" /> : <Link2 size={18} className="text-gray-600" />}
        <AnimatePresence>
          {copied && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap"
            >
              Copied!
            </motion.div>
          )}
        </AnimatePresence>
      </button>

      <a
        href={shareLinks.linkedin}
        target="_blank"
        rel="noopener noreferrer"
        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        aria-label="Share on LinkedIn"
      >
        <Linkedin size={18} className="text-gray-600" />
      </a>

      <a
        href={shareLinks.x}
        target="_blank"
        rel="noopener noreferrer"
        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        aria-label="Share on X"
      >
        <XIcon size={18} className="text-gray-600" />
      </a>

      <a
        href={shareLinks.facebook}
        target="_blank"
        rel="noopener noreferrer"
        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        aria-label="Share on Facebook"
      >
        <Facebook size={18} className="text-gray-600" />
      </a>

      <a
        href={shareLinks.instagram}
        target="_blank"
        rel="noopener noreferrer"
        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        aria-label="Share on Instagram"
      >
        <Instagram size={18} className="text-gray-600" />
      </a>

      {canShare && (
        <button
          onClick={handleNativeShare}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          aria-label="Share"
        >
          <Share2 size={18} className="text-gray-600" />
        </button>
      )}

      <a
        href={shareLinks.email}
        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        aria-label="Share via email"
      >
        <Mail size={18} className="text-gray-600" />
      </a>
    </div>
  );
};

export default SocialShare;
